/* Level Up — 12-question life quiz → personalised Level Up Plan (Claude). */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import TimeLockDialog from "@/components/TimeLockDialog";
import { BillLockDialog } from "@/components/BillLocksSection";
import CareerSection from "@/components/CareerSection";
import NewHomeSection from "@/components/NewHomeSection";
import { api, formatErr } from "@/lib/api";
import { TrendingUp, ArrowRight, ArrowLeft, Check, RotateCw, Car, Briefcase, Home, LineChart, AlertTriangle, Sparkles, Receipt, Lock, Repeat, MapPin } from "lucide-react";

const ACTION_META = {
  bill_lock: { label: "Create Bill Lock", Icon: Receipt },
  time_lock: { label: "Start Time Lock", Icon: Lock },
  auto_commitment: { label: "Add commitment", Icon: Repeat },
  map: { label: "Open Map", Icon: MapPin },
};

const OPTION_ICON = { car: Car, business: Briefcase, house: Home, invest: LineChart };
const LEVEL_COLORS = { 1: "rgb(var(--c-sage))", 2: "rgb(var(--c-gold))", 3: "rgb(var(--c-forest))", 4: "#2F7F8A", 5: "rgb(var(--c-ink))" };

function Quiz({ questions, onDone, onCancel }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const q = questions[i];
  const pick = (opt) => {
    const next = { ...answers, [q.id]: opt };
    setAnswers(next);
    if (i < questions.length - 1) setTimeout(() => setI(i + 1), 160);
  };
  const submit = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/levelup/submit", { answers }); onDone(data); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const complete = questions.every((x) => answers[x.id]);
  return (
    <div className="bg-linen border border-sand rounded-2xl p-6 sm:p-8" data-testid="levelup-quiz">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
        <span data-testid="levelup-progress">Question {i + 1} of {questions.length}</span>
        {onCancel && <button onClick={onCancel} className="hover:text-ink" data-testid="levelup-quiz-cancel">Cancel</button>}
      </div>
      <div className="mt-2 h-1 rounded-full bg-sand-soft"><div className="h-full bg-forest rounded-full transition-all" style={{ width: `${((i + (answers[q.id] ? 1 : 0)) / questions.length) * 100}%` }} /></div>
      <h2 className="font-serif text-2xl sm:text-3xl mt-6" data-testid="levelup-question">{q.q}</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        {q.opts.map((o) => (
          <button key={o} onClick={() => pick(o)}
            className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${answers[q.id] === o ? "bg-forest text-white border-forest" : "bg-white border-sand hover:border-forest"}`}
            data-testid={`levelup-opt-${q.id}-${q.opts.indexOf(o)}`}>{o}</button>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between gap-3">
        <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="inline-flex items-center gap-1.5 text-sm text-moss disabled:opacity-40" data-testid="levelup-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        {i < questions.length - 1 ? (
          <button onClick={() => setI(i + 1)} disabled={!answers[q.id]} className="inline-flex items-center gap-1.5 text-sm text-forest font-medium disabled:opacity-40" data-testid="levelup-next">Next <ArrowRight className="w-4 h-4" /></button>
        ) : (
          <button onClick={submit} disabled={!complete || busy}
            className="bg-forest text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="levelup-submit">
            <Sparkles className="w-4 h-4" /> {busy ? "Building your plan… (~20s)" : "Build my Level Up Plan"}
          </button>
        )}
      </div>
      {err && <div className="mt-3 text-sm text-clay" data-testid="levelup-error">{err}</div>}
    </div>
  );
}

function Plan({ res, onRetake }) {
  const p = res.plan; const color = LEVEL_COLORS[p.level] || "rgb(var(--c-forest))";
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);
  const [done, setDone] = useState({});
  const openNudge = (i, st) => {
    const a = st.action || {};
    if (a.type === "map") { navigate("/map"); return; }
    setNudge({ i, ...a, context: `Level Up step ${i + 1}: ${st.text.slice(0, 80)}${st.text.length > 80 ? "…" : ""}` });
  };
  const finish = () => { if (nudge) setDone((d) => ({ ...d, [nudge.i]: true })); setNudge(null); };
  const stepOf = (st) => (typeof st === "string" ? { text: st, action: { type: "none" } } : st);
  return (
    <div className="space-y-4" data-testid="levelup-plan">
      {nudge?.type === "time_lock" && <TimeLockDialog initialLabel={nudge.name || ""} initialAmount={nudge.amount && nudge.amount <= 500 ? nudge.amount : ""} context={nudge.context} onClose={() => setNudge(null)} onDone={finish} />}
      {nudge?.type === "bill_lock" && <BillLockDialog initial={{ name: nudge.name || "", amount: nudge.amount || "", context: nudge.context }} onClose={() => setNudge(null)} onDone={finish} />}
      <div className="rounded-2xl bg-ink text-cream p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/15 to-transparent pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold"><TrendingUp className="w-3 h-3" /> Your level</div>
            <div className="mt-2 flex items-center gap-3">
              <span className="font-mono text-5xl leading-none" data-testid="levelup-level">{p.level}</span>
              <div>
                <div className="font-serif text-2xl" data-testid="levelup-level-name">{p.level_name}</div>
                <div className="flex gap-1 mt-1">{[1, 2, 3, 4, 5].map((n) => <span key={n} className="h-1.5 w-6 rounded-full" style={{ background: n <= p.level ? color : "rgb(var(--c-ink-soft))" }} />)}</div>
              </div>
            </div>
            {res.previous_level != null && (
              <div className={`mt-2 text-xs ${res.levelled_up ? "text-mint-deep" : "text-sage-light"}`} data-testid="levelup-delta">
                {res.levelled_up ? `Levelled up from ${res.previous_level} — keep going.` : res.level_delta < 0 ? `Down from level ${res.previous_level}. Let's fix that.` : `Holding at level ${res.previous_level}.`}
              </div>
            )}
          </div>
          <div className="text-right text-xs text-sage-light">
            <div>Plan from {new Date(res.created_at).toLocaleDateString()}</div>
            {res.can_retake
              ? <button onClick={onRetake} className="mt-2 inline-flex items-center gap-1.5 bg-gold text-white rounded-md px-3 py-1.5 text-xs font-medium" data-testid="levelup-retake"><RotateCw className="w-3 h-3" /> Retake quiz</button>
              : <div className="mt-1" data-testid="levelup-retake-countdown">Retake in {res.days_until_retake} day{res.days_until_retake === 1 ? "" : "s"}</div>}
          </div>
        </div>
        <h2 className="relative font-serif text-3xl sm:text-4xl mt-6" data-testid="levelup-headline">{p.headline}</h2>
        <p className="relative text-sm text-sand mt-2 max-w-2xl" data-testid="levelup-summary">{p.summary}</p>
      </div>

      <div className="rounded-2xl bg-forest text-white p-6" data-testid="levelup-next-move">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E8D8B5]">Your next move</div>
        <div className="font-serif text-xl sm:text-2xl mt-1.5">{p.next_move}</div>
        <ol className="mt-4 space-y-3">
          {p.steps.map((raw, i) => { const st = stepOf(raw); const meta = ACTION_META[st.action?.type]; return (
            <li key={i} className="flex gap-3 text-sm items-start" data-testid={`levelup-step-${i}`}>
              <span className="font-mono text-[#E8D8B5] shrink-0">0{i + 1}</span>
              <div className="flex-1 min-w-0">
                <span>{st.text}</span>
                {meta && (
                  <div className="mt-1.5">
                    {done[i]
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-mint-deep" data-testid={`levelup-step-done-${i}`}><Check className="w-3 h-3" /> Done — nice.</span>
                      : <button onClick={() => openNudge(i, st)}
                          className="inline-flex items-center gap-1.5 bg-cream text-ink rounded-md px-2.5 py-1 text-[11px] font-medium hover:bg-white transition-colors"
                          data-testid={`levelup-step-action-${i}`}>
                          <meta.Icon className="w-3 h-3" strokeWidth={2} /> {meta.label}{st.action.amount ? ` · $${Number(st.action.amount).toLocaleString()}` : ""} <ArrowRight className="w-3 h-3" />
                        </button>}
                  </div>
                )}
              </div>
            </li>
          ); })}
        </ol>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">What each option does to your net worth</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {p.options.map((o) => { const Icon = OPTION_ICON[o.key] || Check; return (
            <div key={o.key} className="bg-linen border border-sand rounded-xl p-5" data-testid={`levelup-option-${o.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 font-serif text-lg"><Icon className="w-4 h-4 text-forest" strokeWidth={1.75} /> {o.title}</div>
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-ink text-white shrink-0">{o.verdict}</span>
              </div>
              <p className="text-sm text-moss mt-2">{o.net_worth}</p>
            </div>
          ); })}
        </div>
      </div>

      {p.watch_out && (
        <div className="flex items-start gap-3 bg-clay-light border border-clay/30 rounded-xl p-4 text-sm text-ink" data-testid="levelup-watch-out">
          <AlertTriangle className="w-4 h-4 text-clay shrink-0 mt-0.5" /> {p.watch_out}
        </div>
      )}
      <div className="text-[10px] text-sage">Coaching generated from your quiz + your real account data · {p.source === "fallback" ? "starter plan" : "AI coach"} · not financial advice</div>
    </div>
  );
}

export default function LevelUp() {
  const [questions, setQuestions] = useState([]);
  const [res, setRes] = useState(null);
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("loading");

  const load = useCallback(async () => {
    const [{ data: q }, { data: p }] = await Promise.all([api.get("/levelup/questions"), api.get("/levelup/plan")]);
    setQuestions(q.questions); setRes(p.plan); setHistory(p.history || []);
    setMode(p.plan ? "plan" : "intro");
  }, []);
  useEffect(() => { load().catch(() => setMode("intro")); }, [load]);

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="levelup-page">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Level Up · Life advisor</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Where are you, and what&apos;s next?</h1>
        <p className="text-moss mt-3 max-w-2xl leading-relaxed">
          Thirteen quick questions about your life today. We combine them with your real vaults, streaks and spending to tell you your level, your single next move, and what a car, a business, a home or investing would do to your net worth.
        </p>

        <div className="mt-8">
          {mode === "loading" && <div className="font-mono text-sm text-moss">loading…</div>}
          {mode === "intro" && (
            <div className="bg-linen border border-sand rounded-2xl p-8 text-center" data-testid="levelup-intro">
              <TrendingUp className="w-8 h-8 text-gold mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl mt-4">Take the 90-second quiz</h2>
              <p className="text-sm text-moss mt-2">Retake it every 30 days and we&apos;ll tell you whether you levelled up.</p>
              <button onClick={() => setMode("quiz")} className="mt-6 bg-forest text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-forest-deep inline-flex items-center gap-2" data-testid="levelup-start">
                Start the quiz <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {mode === "quiz" && <Quiz questions={questions} onDone={(d) => { setRes(d); setMode("plan"); load().catch(() => {}); }} onCancel={res ? () => setMode("plan") : null} />}
          {mode === "plan" && res && <Plan res={res} onRetake={() => setMode("quiz")} />}
        </div>

        {history.length > 1 && (
          <div className="mt-10" data-testid="levelup-history">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">Your level over time</div>
            <div className="flex items-end gap-2 h-24">
              {history.map((h) => (
                <div key={h.id} className="flex-1 flex flex-col items-center gap-1" title={h.headline}>
                  <div className="w-full rounded-t-md" style={{ height: `${(h.level / 5) * 100}%`, background: LEVEL_COLORS[h.level] }} />
                  <div className="text-[9px] text-sage font-mono">{new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <CareerSection />

        <NewHomeSection />
      </div>
    </div>
  );
}
